/**
 * ================ API error handling utilities ================
 *
 * This module exports helpers that can be used to check if API
 * response errors are some specific error cases.
 *
 * NOTE: most of the functions are tied to an endpoint, and should not
 * be used to check error responses from any other endpoint. Check the
 * name and the docstring of the function to ensure correct usage.
 */

import {
  ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND,
  ERROR_CODE_TRANSACTION_INVALID_TRANSITION,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER,
  ERROR_CODE_PAYMENT_FAILED,
  ERROR_CODE_CHARGE_ZERO_PAYIN,
  ERROR_CODE_EMAIL_TAKEN,
  ERROR_CODE_EMAIL_NOT_FOUND,
  ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS,
  ERROR_CODE_UPLOAD_OVER_LIMIT,
  ERROR_CODE_MISSING_STRIPE_ACCOUNT,
  ERROR_CODE_TRANSACTION_BOOKING_TIME_NOT_AVAILABLE,
  ERROR_CODE_TRANSACTION_LISTING_INSUFFICIENT_STOCK,
  ERROR_CODE_STOCK_OLD_TOTAL_MISMATCH,
  ERROR_CODE_NOT_FOUND,
} from './types';
// NOTE: This file imports types.js, which may lead to circular dependency

const errorAPIErrors = error => {
  return error && error.apiErrors ? error.apiErrors : [];
};

const hasErrorWithCode = (error, code) => {
  return errorAPIErrors(error).some(apiError => {
    return apiError.code === code;
  });
};

/**
 * return apiErrors from error response
 */
const responseAPIErrors = error => {
  return error && error.data && error.data.errors ? error.data.errors : [];
};

/**
 * 429 Too Many Requests error
 */
export const isTooManyRequestsError = error => error && error.status === 429;

/**
 * Check if the given API error (from `sdk.currentuser.create()`) is
 * due to the email address already being in use.
 */
export const isSignupEmailTakenError = error => hasErrorWithCode(error, ERROR_CODE_EMAIL_TAKEN);

/**
 * Check if the given API error (from `sdk.currentuser.changeEmail()`) is
 * due to the email address already being in use.
 */
export const isChangeEmailTakenError = error => hasErrorWithCode(error, ERROR_CODE_EMAIL_TAKEN);

/**
 * Check if the given API error (from
 * `sdk.currentUser.sendVerificationEmail()`) is due to too many
 * active email verification requests.
 *
 * There qre only a specific amount of active verification requests
 * allowed, and the user has to wait for them to expire to be able to
 * request sending new verification emails.
 */
export const isTooManyEmailVerificationRequestsError = error =>
  hasErrorWithCode(error, ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS);

/**
 * Check if the given API error (from
 * `sdk.images.upload()`) is due to the image being over
 * the size limit.
 */
export const isUploadImageOverLimitError = error =>
  hasErrorWithCode(error, ERROR_CODE_UPLOAD_OVER_LIMIT);

/**
 * Check if the given API error (from
 * `sdk.stock.compareAndSet()`) is due to the oldTotal being wrong.
 */
export const isOldTotalMismatchStockError = error =>
  hasErrorWithCode(error, ERROR_CODE_STOCK_OLD_TOTAL_MISMATCH);

/**
 * Check if the given API error (from `sdk.passwordReset.request()`)
 * is due to no user having the given email address.
 */
export const isPasswordRecoveryEmailNotFoundError = error =>
  hasErrorWithCode(error, ERROR_CODE_EMAIL_NOT_FOUND);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to the listing
 * being closed or deleted.
 */
export const isTransactionInitiateListingNotFoundError = error =>
  hasErrorWithCode(error, ERROR_CODE_NOT_FOUND);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to missign Stripe
 * connection from the listing author.
 */
export const isTransactionInitiateMissingStripeAccountError = error =>
  hasErrorWithCode(error, ERROR_CODE_MISSING_STRIPE_ACCOUNT);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to selected booking
 * time already being booked.
 */
export const isTransactionInitiateBookingTimeNotAvailableError = error =>
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_BOOKING_TIME_NOT_AVAILABLE);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to insufficient stock.
 */
export const isTransactionInitiateListingInsufficientStockError = error =>
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_LISTING_INSUFFICIENT_STOCK);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to payment being zero.
 */
export const isTransactionZeroPaymentError = error =>
  hasErrorWithCode(error, ERROR_CODE_CHARGE_ZERO_PAYIN);

/**
 * Check if the error indicates that the listing price changed between
 * checkout page load and payment submission — our custom 409 guard in
 * initiate-privileged.js / transition-privileged.js.
 */
export const isTransactionInitiatePriceChangedError = error =>
  error?.status === 409 && error?.statusText === 'transaction-price-changed';

/**
 * Check if the given API error (from `sdk.transaction.initiate()`) is
 * due to the transaction total amount being too low for Stripe.
 */
export const isTransactionInitiateAmountTooLowError = error => {
  const isZeroPayment = isTransactionZeroPaymentError(error);

  const tooLowAmount = errorAPIErrors(error).some(apiError => {
    const isPaymentFailedError =
      apiError.status === 402 && apiError.code === ERROR_CODE_PAYMENT_FAILED;
    let isAmountTooLow = false;

    try {
      // TODO: This is a temporary solution until a proper error code
      // for this specific error is received in the response.
      const msg = apiError.meta.stripeMessage;
      isAmountTooLow =
        msg.startsWith('Amount must be at least') ||
        msg.startsWith('Amount must convert to at least');
    } catch (e) {
      // Ignore
    }

    return isPaymentFailedError && isAmountTooLow;
  });

  return isZeroPayment || tooLowAmount;
};

/**
 * Check if the given API error (from `sdk.transaction.initiate()`) is
 * due to the transaction charge creation disabled by Stripe.
 */
export const isTransactionChargeDisabledError = error => {
  const chargeCreationDisabled = errorAPIErrors(error).some(apiError => {
    const isPaymentFailedError =
      apiError.status === 402 && apiError.code === ERROR_CODE_PAYMENT_FAILED;

    let isChargeCreationDisabled = false;
    try {
      const msg = apiError.meta.stripeMessage;
      isChargeCreationDisabled =
        msg.startsWith('Your account cannot currently make charges.') ||
        msg.match(/verification.disabled_reason/);
    } catch (e) {
      // Ignore
    }

    return isPaymentFailedError && isChargeCreationDisabled;
  });

  return chargeCreationDisabled;
};

/**
 * Check if the given API error (from `sdk.transaction.initiate()`) is
 * due to other error in Stripe.
 */
export const transactionInitiateOrderStripeErrors = error => {
  if (error) {
    return errorAPIErrors(error).reduce((messages, apiError) => {
      const isPaymentFailedError =
        apiError.status === 402 && apiError.code === ERROR_CODE_PAYMENT_FAILED;
      const hasStripeError = apiError && apiError.meta && apiError.meta.stripeMessage;
      const stripeMessageMaybe =
        isPaymentFailedError && hasStripeError ? [apiError.meta.stripeMessage] : [];
      return [...messages, ...stripeMessageMaybe];
    }, []);
  }
  return null;
};

/**
 * Check if the given API error (from `sdk.transactions.transition(id, transition, params)`)
 * is due to invalid transition attempt.
 */
export const isTransactionsTransitionInvalidTransition = error =>
  error &&
  error.status === 409 &&
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_INVALID_TRANSITION);

/**
 * Check if the given API error (from `sdk.transactions.transition(id, transition, params)`)
 * is due to already sent review.
 */
export const isTransactionsTransitionAlreadyReviewed = error =>
  error &&
  error.status === 409 &&
  (hasErrorWithCode(error, ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER) ||
    hasErrorWithCode(error, ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER));

/**
 * Check if the given API error (from `sdk.currentUser.changeEmail(params)`)
 * is due to giving wrong password.
 */
export const isChangeEmailWrongPassword = error => error && error.status === 403;

/**
 * Check if the given API error (from `sdk.currentUser.changePassword(params)`)
 * is due to giving wrong password.
 */
export const isChangePasswordWrongPassword = error => error && error.status === 403;

/**
 * Check if the given API error (from
 * 'sdk.stripeAccount.create(payoutDetails)') is due to
 * invalid postal code in the given country.
 */
export const isStripeInvalidPostalCode = error => {
  const msgRe = /^Invalid [A-Z]{2} postal code$/;
  return errorAPIErrors(error).some(apiError => {
    // Stripe doesn't seem to give an error code for this specific
    // case, so we have to recognize it from the message.
    const msg = apiError.meta && apiError.meta.stripeMessage ? apiError.meta.stripeMessage : '';
    return msgRe.test(msg);
  });
};

export const isStripeError = error => {
  return errorAPIErrors(error).some(apiError => {
    // Stripe doesn't seem to give an error code for this specific
    // case, so we have to recognize it from the message.
    return !!(apiError.meta && apiError.meta.stripeMessage);
  });
};

// Error "type" values that Stripe.js itself throws (e.g. from
// stripe.confirmCardPayment()), as opposed to errors coming from the
// Sharetribe Marketplace API (which use status/statusText/apiErrors).
// See: https://stripe.com/docs/api#errors
const STRIPE_JS_ERROR_TYPES = [
  'card_error',
  'validation_error',
  'invalid_request_error',
  'api_connection_error',
  'api_error',
  'authentication_error',
  'idempotency_error',
  'rate_limit_error',
];

/**
 * Check if the given error was thrown directly by Stripe.js (e.g. a
 * declined card from stripe.confirmCardPayment()), rather than being
 * an error from the Sharetribe Marketplace API.
 *
 * Errors like this are already surfaced with a specific, user-facing
 * message via confirmCardPaymentError/StripePaymentForm, so callers can
 * use this to avoid showing a second, generic error for the same failure.
 */
export const isStripeJsError = error => STRIPE_JS_ERROR_TYPES.includes(error?.type);

/**
 * Check if the given transition error is
 * due to no quantity information in the transition params.
 */
export const isTransitionQuantityInfoMissingError = error =>
  error?.status === 400 &&
  error?.statusText.startsWith('Error: transition should contain quantity information');

/**
 * The checkout submit timed out on the client (Promise.race in
 * CheckoutPageWithPayment). The payment may still be completing on Stripe /
 * Sharetribe, so this must NOT be presented as an outright failure.
 */
export const isCheckoutTimeoutError = error => error?.message === 'CHECKOUT_TIMEOUT';

/**
 * The booking's end date is already in the past. Raised client-side before
 * submit, and also returned by the Marketplace API as
 * `transaction-booking-end-is-in-the-past`.
 */
export const isBookingDatesInPastError = error =>
  error?.message === 'BOOKING_DATES_IN_PAST' ||
  errorAPIErrors(error).some(e => e?.code === 'transaction-booking-end-is-in-the-past');

/**
 * A checkout submit failed for a reason a plain re-submit can recover from
 * (network blip, request timeout, transient transaction lock, rate limit) as
 * opposed to a structural problem (deleted listing, price changed, amount too
 * low, Stripe account not set up, dates in the past) where re-sending the same
 * request is pointless. Used to decide whether to keep the payment form mounted
 * so the user can simply try again in place.
 */
export const isRetriableCheckoutError = error => {
  if (!error) return false;
  const isStructural =
    isTransactionInitiateListingNotFoundError(error) ||
    isTransactionInitiateMissingStripeAccountError(error) ||
    isTransactionInitiatePriceChangedError(error) ||
    isTransactionInitiateAmountTooLowError(error) ||
    isTransactionChargeDisabledError(error) ||
    isTransactionInitiateBookingTimeNotAvailableError(error) ||
    isTransactionInitiateListingInsufficientStockError(error) ||
    isTransitionQuantityInfoMissingError(error) ||
    isBookingDatesInPastError(error);
  return !isStructural;
};

/**
 * Check if error is due to authentication issues (e.g., logged out in another tab)
 * Can be used universally across the application
 * 
 * @param {Object} error - The error object from API
 * @returns {boolean} - True if it's an authentication error
 */
export const isAuthenticationError = (error) => {
  if (!error) return false;

  // Direct auth status codes
  if (error.status === 401 || error.status === 403) {
    return true;
  }

  // Sharetribe API errors for missing/invalid session
  const apiErrors = errorAPIErrors(error);
  const hasAuthError = apiErrors.some(e =>
    e.code === 'forbidden' ||
    e.code === 'unauthorized' ||
    e.title?.toLowerCase().includes('authentication') ||
    e.title?.toLowerCase().includes('not authorized')
  );

  if (hasAuthError) {
    return true;
  }

  // Check error message and statusText
  const message = error.message?.toLowerCase() || '';
  const statusText = error.statusText?.toLowerCase() || '';

  return message.includes('unauthorized') ||
    message.includes('forbidden') ||
    message.includes('authentication') ||
    statusText.includes('unauthorized') ||
    statusText.includes('forbidden');
};

/**
 * Get user-friendly error message based on error type
 * Universal function that can be used across the application
 * 
 * @param {Object} error - The error object from API
 * @param {Object} intl - React Intl object for translations
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error, intl) => {
  if (!error) return '';

  // Priority: Check for authentication errors (logged out in another tab)
  if (isAuthenticationError(error)) {
    return intl.formatMessage({
      id: 'errors.authentication',
      defaultMessage: 'Your session has expired or you were logged out in another tab. Please refresh the page to continue.'
    });
  }

  // Check for specific validation errors
  const apiErrors = errorAPIErrors(error);
  const validationError = apiErrors.find(e =>
    e.code === 'validation-invalid-params' ||
    e.code === 'validation-invalid-value'
  );

  if (validationError) {
    return intl.formatMessage({
      id: 'errors.validation',
      defaultMessage: 'Please check your input and try again. If the problem persists, please refresh the page and try again.'
    });
  }

  // Check for network errors
  if (error.status === 0 || error.message?.includes('Network')) {
    return intl.formatMessage({
      id: 'errors.network',
      defaultMessage: 'Network error. Please check your internet connection and try again.'
    });
  }

  // Check for server errors
  if (error.status >= 500) {
    return intl.formatMessage({
      id: 'errors.server',
      defaultMessage: 'Server error. Please try again in a few moments.'
    });
  }

  // Generic error with status text if available
  if (error.statusText) {
    return intl.formatMessage({
      id: 'errors.withDetails',
      defaultMessage: 'Error: {details}'
    }, { details: error.statusText });
  }

  // Fallback generic message
  return intl.formatMessage({
    id: 'errors.generic',
    defaultMessage: 'Something went wrong. Please try again.'
  });
};

export const storableError = err => {
  const error = err || {};
  const { name, message, status, statusText } = error;
  // Status, statusText, and data.errors are (possibly) added to the error object by SDK
  const apiErrors = responseAPIErrors(error);

  // Returned object is the same as prop type check in util/types -> error
  return {
    type: 'error',
    name,
    message,
    status,
    statusText,
    apiErrors,
  };
};
